import {Injectable} from '@angular/core';
import {HttpHandler, HttpInterceptor, HttpRequest, HttpEvent, HttpErrorResponse} from '@angular/common/http';
import {AuthService} from './auth.service';
import {catchError, throwError, switchMap, filter, take, BehaviorSubject, Observable} from 'rxjs';
import {environment} from '../../../environments/environment';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  constructor(private auth: AuthService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApiRequest = this.isApiRequest(req.url);
    const isRefreshRequest = req.url.includes('/auth/refresh-token');
    const authorizationHeader = req.headers.get('Authorization');
    const hasValidAuthorization = !!authorizationHeader
      && !/^Bearer\s+(null|undefined)?$/i.test(authorizationHeader.trim());

    let request = req;

    if (isApiRequest) {
      request = request.clone({withCredentials: true});
    }

    if (isApiRequest && !isRefreshRequest && !hasValidAuthorization) {
      const accessToken = this.auth.getAccessToken();
      if (accessToken) {
        request = this.addTokenHeader(request, accessToken);
      }
    }

    return next.handle(request).pipe(
      catchError(error => {
        if (this.shouldAttemptRefresh(error, request, isApiRequest, isRefreshRequest)) {
          return this.handle401Error(request, next);
        }
        return throwError(() => error);
      })
    );
  }

  private shouldAttemptRefresh(
    error: unknown,
    request: HttpRequest<any>,
    isApiRequest: boolean,
    isRefreshRequest: boolean
  ): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 401) {
      return false;
    }

    if (!isApiRequest || isRefreshRequest) {
      return false;
    }

    if (!this.auth.getAccessToken()) {
      return false;
    }

    return !request.url.includes('/auth/authenticate')
      && !request.url.includes('/auth/logout')
      && !request.url.includes('/auth/reset/')
      && !request.url.includes('/guest/');
  }

  /**
   * 🔄 Manejo de error 401 - Token expirado
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      return this.auth.refreshToken().pipe(
        switchMap(response => {
          const newAccessToken = response.data?.accessToken;
          if (!newAccessToken) {
            throw new Error('Refresh sin access token');
          }

          this.isRefreshing = false;
          this.refreshTokenSubject.next(newAccessToken);
          return next.handle(this.addTokenHeader(request, newAccessToken));
        }),
        catchError(error => {
          this.isRefreshing = false;
          this.auth.handleAuthFailure();
          return throwError(() => error);
        })
      );
    }

    // 🔄 Si ya se está refrescando, esperar
    return this.refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next.handle(this.addTokenHeader(request, token)))
    );
  }

  /**
   * 🔑 Agregar token al header
   */
  private addTokenHeader(request: HttpRequest<any>, token: string | null): HttpRequest<any> {
    if (token) {
      return request.clone({
        headers: request.headers.set('Authorization', `Bearer ${token}`),
        withCredentials: request.withCredentials || this.isApiRequest(request.url)
      });
    }
    return request;
  }

  private isApiRequest(url: string): boolean {
    const normalizedApiBase = this.normalizeUrl(environment.apiBase);
    const normalizedRequestUrl = this.normalizeUrl(url);

    if (normalizedApiBase && normalizedRequestUrl.startsWith(normalizedApiBase)) {
      return true;
    }

    const requestPath = this.extractPath(url);
    return requestPath === '/api' || requestPath.startsWith('/api/');
  }

  private normalizeUrl(url: string | null | undefined): string {
    return (url ?? '').trim().replace(/\/+$/, '');
  }

  private extractPath(url: string): string {
    if (!url) {
      return '';
    }

    if (/^https?:\/\//i.test(url)) {
      try {
        return new URL(url).pathname;
      } catch {
        return '';
      }
    }

    const noQuery = url.split('?')[0].split('#')[0];
    return `/${noQuery}`.replace(/\/+/g, '/');
  }
}
